import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { Button, Divider, Header } from 'semantic-ui-react'
import { useAppDispatch } from '../../../utils/typeHelpers'

import TaxProfileModal from './TaxProfileModal'
import { fetchAdminUsersDocuments } from '../../../actions/admin/userDocumentsActions'
import { fetchAdminUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { getUserById } from '../../../selectors/user.selectors'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { useReselector } from '../../../utils/sharedHooks'
import { TaxProfileContent } from './helpers'
import {
  fetchFinancialProfileSnapshotForUser,
  fetchUserSnapshot,
  RecordSnapshotEntityName,
} from '@/features/admin/record-snapshots/recordSnapshot.slice'
import { getFinancialProfileRecordSnapshotForUserAndYear } from '@/features/admin/record-snapshots/recordSnapshot.selector'
import { getIsFetching } from '../../../reducers/fetch'
import { DATE_FORMATS_LUXON } from '../../../utils/dateHelpers'

const TaxYearSnapshot = ({ userId }: { userId: number }) => {
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)

  const currentYearFPSnapshot = useReselector(
    getFinancialProfileRecordSnapshotForUserAndYear,
    { userId, year: currentTaxYear }
  )
  const isLoadingCurrentYearFPSnapshot = useReselector(
    getIsFetching,
    fetchUserSnapshot(userId, RecordSnapshotEntityName.financialProfile)
  )

  const thisYear = DateTime.now().year

  if (thisYear !== Number(currentTaxYear)) {
    return (
      <div>
        <Header as="h4">{currentTaxYear} Tax Information Snapshot</Header>
        <div style={{ padding: '1em' }}>
          <div style={{ fontStyle: 'italic' }}>
            {!isLoadingCurrentYearFPSnapshot && currentYearFPSnapshot && (
              <TaxProfileContent
                profile={currentYearFPSnapshot.data}
                userId={userId}
              />
            )}
          </div>
        </div>
        <Divider />
        <Divider />
      </div>
    )
  }
  return null
}

const TaxProfileCard = ({ userId }: { userId: number }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchAdminUserDocumentCategoriesIfNeeded())
    dispatch(fetchAdminUsersDocuments(userId))
    dispatch(fetchFinancialProfileSnapshotForUser({ userId }))
  }, [dispatch, userId])

  const user = useReselector(getUserById, userId)

  if (!user?.financialProfile) {
    return null
  }

  const profile = user.financialProfile

  return (
    <>
      <div className="flexbox">
        <Header as="h4">Current Tax Information</Header>
        <Button className="link" onClick={() => setModalOpen(true)}>
          Edit
        </Button>
        {modalOpen && user && (
          <TaxProfileModal
            user={user}
            open={modalOpen}
            close={() => setModalOpen(false)}
          />
        )}
      </div>
      <div style={{ padding: '1em' }}>
        <div style={{ fontStyle: 'italic' }}>
          <p>
            Last Reviewed By Customer:{' '}
            {profile?.taxProfileLastReviewed
              ? DateTime.fromISO(profile.taxProfileLastReviewed, {
                  zone: 'utc',
                }).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)
              : 'Never'}
          </p>
          <p>
            Last Updated:{' '}
            {profile?.updatedAt
              ? DateTime.fromISO(profile.updatedAt).toFormat(
                  DATE_FORMATS_LUXON.DISPLAY_SHORT
                )
              : 'Never'}
          </p>
          <TaxProfileContent profile={profile} userId={userId} />
        </div>
      </div>
      <Divider />
      <Divider />
      <TaxYearSnapshot userId={userId} />
    </>
  )
}

export default TaxProfileCard
